import apiClient from '../axios.js';

/**
 * 門市管理 API
 */
export const storeService = {
  /**
   * 查詢門市列表
   * @param {Object} params - 查詢參數
   * @param {number} params.page - 頁碼
   * @param {number} params.pageSize - 每頁筆數
   * @param {string} params.status - 門市狀態 (active, inactive, closed)
   * @param {string} params.city - 城市
   * @param {boolean} params.bookingEnabled - 是否開放預約
   * @param {string} params.search - 搜尋關鍵字
   * @param {string} params.sortBy - 排序欄位
   * @param {string} params.order - 排序方向
   * @returns {Promise<Object>} 門市列表
   */
  getStores: async (params = {}) => {
    const response = await apiClient.get('/stores', { params });
    return response;
  },

  /**
   * 查詢門市詳情
   * @param {string} storeId - 門市 ID
   * @returns {Promise<Object>} 門市詳情
   */
  getStore: async (storeId) => {
    const response = await apiClient.get(`/stores/${storeId}`);
    return response;
  },

  /**
   * 新增門市
   * @param {Object} storeData - 門市資料
   * @returns {Promise<Object>} 新增結果
   */
  createStore: async (storeData) => {
    const response = await apiClient.post('/stores', storeData);
    return response;
  },

  /**
   * 更新門市
   * @param {string} storeId - 門市 ID
   * @param {Object} storeData - 門市資料
   * @returns {Promise<Object>} 更新結果
   */
  updateStore: async (storeId, storeData) => {
    const response = await apiClient.put(`/stores/${storeId}`, storeData);
    return response;
  },

  /**
   * 刪除門市
   * @param {string} storeId - 門市 ID
   * @returns {Promise<Object>} 刪除結果
   */
  deleteStore: async (storeId) => {
    const response = await apiClient.delete(`/stores/${storeId}`);
    return response;
  },

  /**
   * 更新門市狀態
   * @param {string} storeId - 門市 ID
   * @param {string} status - 新狀態 (active, inactive, closed)
   * @returns {Promise<Object>} 更新結果
   */
  updateStoreStatus: async (storeId, status) => {
    const response = await apiClient.patch(`/stores/${storeId}/status`, { status });
    return response;
  },

  /**
   * 查詢門市數量限制資訊
   * @returns {Promise<Object>} 限制資訊
   */
  getStoreLimit: async () => {
    const response = await apiClient.get('/stores/limits');
    return response;
  },

  /**
   * 查詢門市營業時間
   * @param {string} storeId - 門市 ID
   * @returns {Promise<Object>} 營業時間列表
   */
  getStoreHours: async (storeId) => {
    const response = await apiClient.get(`/stores/${storeId}/hours`);
    return response;
  },

  /**
   * 設定門市營業時間（批量）
   * @param {string} storeId - 門市 ID
   * @param {Array} hours - 營業時間陣列
   * @returns {Promise<Object>} 設定結果
   */
  setStoreHours: async (storeId, hours) => {
    const response = await apiClient.post(`/stores/${storeId}/hours`, { hours });
    return response;
  },

  /**
   * 更新特定天的營業時間
   * @param {string} storeId - 門市 ID
   * @param {number} dayOfWeek - 星期幾 (0=週日, 1=週一, ..., 6=週六)
   * @param {Object} hoursData - 營業時間資料
   * @returns {Promise<Object>} 更新結果
   */
  updateStoreHours: async (storeId, dayOfWeek, hoursData) => {
    const response = await apiClient.put(`/stores/${storeId}/hours/${dayOfWeek}`, hoursData);
    return response;
  },

  /**
   * 刪除門市所有營業時間
   * @param {string} storeId - 門市 ID
   * @returns {Promise<Object>} 刪除結果
   */
  deleteStoreHours: async (storeId) => {
    const response = await apiClient.delete(`/stores/${storeId}/hours`);
    return response;
  },
};
